import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent } from '../components/common/confirm-dialog/confirm-dialog.component';
import { SseService } from './SseService';
import { OAuthService } from './oauth.service';
import { BrokerService } from './broker.services';
import { OrigenOrden } from '../shared/pipes/origen-orden.pipe';

/**
 * Envío manual de órdenes a IBKR desde los diálogos de Operación de Entrada / Salida:
 * registra la orden en BD (broker/orderNuevo), la envía a /iserver y resuelve las
 * confirmaciones (/iserver/reply) que pida IBKR hasta obtener un order_status.
 */
@Injectable({ providedIn: 'root' })
export class IbkrOrdenService {

  constructor(
    private sse: SseService,
    private oauth: OAuthService,
    private dialog: MatDialog,
    private cookies: CookieService,
    private brokerService: BrokerService,
    private toastr: ToastrService,
  ) { }

  /** true si IBKR aceptó la orden; false si se rechazó, se canceló una confirmación o falló. */
  async registrarYEnviar(ordersData: { orders: any[] }, cuenta: string, panelClass?: string, origen?: OrigenOrden): Promise<boolean> {
    const licencia = this.cookies.get('auth_token');
    try {
      // El origen solo se registra en BD: ordersData va tal cual a IBKR
      const registro: any = await firstValueFrom(this.sse.apiPostNew('broker/orderNuevo', ordersData.orders.map(o => ({ ...o, origen })), licencia));
      if (!registro?.status) {
        this.toastr.error(registro?.msg, 'Error', { timeOut: 30000 });
        return false;
      }

      let respuesta: any = await firstValueFrom(this.oauth.apiPostIbrk('/iserver/account/' + cuenta + '/orders ', ordersData));
      while (true) {
        const orden = Array.isArray(respuesta) ? respuesta[0] : null;
        if (orden?.message) {
          if (!await this.confirmar(orden.message, panelClass)) return false;
          respuesta = await firstValueFrom(this.oauth.apiPostIbrk('/iserver/reply/' + orden.id, { confirmed: true }));
          continue;
        }
        if (orden?.order_status) {
          const actualizado: any = await firstValueFrom(this.sse.apiPostNew('broker/actualizarOrden', respuesta, licencia));
          if (actualizado?.status) this.brokerService.updatecargarListas(true);
          this.toastr.success('Se registró operación: ' + orden.order_id + ' ' + orden.order_status, '', { timeOut: 3000 });
          return true;
        }
        const error = respuesta?.cqe?.post_payload?.rejections?.[0] ?? respuesta?.error;
        if (error) await this.avisar(error, panelClass);
        return false;
      }
    } catch (err: any) {
      await this.avisar(err?.error?.error ?? err?.error?.message ?? err?.message ?? 'Error enviando la orden a IBKR', panelClass);
      return false;
    }
  }

  private confirmar(msj: string, panelClass?: string): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, { width: '380px', panelClass, data: { msj } });
    return firstValueFrom(ref.afterClosed()).then(result => !!result);
  }

  private avisar(msj: any, panelClass?: string): Promise<boolean> {
    const texto = typeof msj === 'string' ? msj : JSON.stringify(msj);
    const ref = this.dialog.open(ConfirmDialogComponent, { width: '380px', panelClass, data: { msj: texto, titulo: 'IBKR', ocultarCancelar: true } });
    return firstValueFrom(ref.afterClosed()).then(() => true);
  }
}
